import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { FeatureFlags } from '@/lib/api'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

interface FeatureGuardProps {
  /** Feature flag that must be enabled. */
  flag: keyof FeatureFlags
  /** What to redirect to when the flag is disabled. Default: parent index. */
  fallbackPath?: string
  children: ReactNode
}

/**
 * Route-level guard that checks a feature flag before rendering children.
 * While loading, renders nothing (avoids flash). On disabled, redirects.
 */
export function FeatureGuard({ flag, fallbackPath, children }: FeatureGuardProps) {
  const { data: flags, isLoading } = useFeatureFlags()

  if (isLoading) return null

  const enabled = flags?.[flag] ?? true
  if (!enabled) {
    return <Navigate to={fallbackPath ?? '..'} replace />
  }

  return <>{children}</>
}
